/* eslint-disable react-hooks/exhaustive-deps */
/*
 * File: AssignedRule.tsx
 * Project: telly-monster
 * -----
 * Last Modified: 27 Apr 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Dropdown, Form, Menu, message, Modal, Row, Space, Spin, Typography } from 'antd';
import { StepControls } from 'components/atoms/StepControls';
import { LoaderWithMessage } from 'components/common/LoaderWithMessage';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import { AddressPayload } from 'models/Address';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { useCreateAddressMutation } from 'redux/services/chuckieSue/addressesApi';
import { ReduxState } from 'redux/store';

export const CreateAddressPage: React.FC = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();
  const [createAddress, { isLoading: isAddressCreating }] = useCreateAddressMutation();
  const { company } = useSelector((state: ReduxState) => state.app);
  const { selectedBusiness } = useSelector((state: ReduxState) => state.selectedBusiness);
  const { isAddressOveridden } = useSelector((state: ReduxState) => state.form);
  const [isClose, setIsClose] = useState(false);

  function trimObjectValues<T>(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    const trimmedObj = {} as T;

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        if (typeof value === 'string') {
          (trimmedObj as any)[key] = value.trim() as any;
        } else {
          (trimmedObj as any)[key] = value;
        }
      }
    }

    return trimmedObj;
  }

  /* ******************** API Hooks ******************** */
  const formik = useFormik<AddressPayload>({
    enableReinitialize: true,
    // validationSchema: addressSchema,
    initialValues: {
      addressType: [],
      name: '',
      city: '',
      code: '',
      contactEmail: null,
      contactName: '',
      contactPhone: '',
      alpha2Code: '',
      postalCode: '',
      street1: '',
      street2: '',
      localCode: '',
      latitude: null,
      longitude: null,
      businessId: company ?? '',
      locationId: '',
      externalLocationCode: '',
      suppressErpAddressLinking: undefined,
      suppressLatitudeLongitudeLookup: undefined,
      excludeFromPremiumLocalDelivery: undefined,
      thirdPartyVendor: '',
      country: {
        alpha2Code: '',
        alpha3Code: '',
        fullName: '',
        shortName: ''
      },
      subdivision: {
        category: '',
        name: '',
        localCode: ''
      },
      warehouseType: '',
      region: ''
    },
    onSubmit: async (values) => {
      if (!selectedBusiness) {
        message.error('An error occured, the team has been notified');
        return;
      }

      try {
        const responseAddress = await createAddress({
          bussinessId: selectedBusiness.id,
          payload: trimObjectValues(values),
          skipValidation: Boolean(formik.values.latitude && formik.values.longitude ? true : false) || isAddressOveridden
        }).unwrap();
        message.success(`Address was successfully created!`);
        if (isClose) navigate(`/business/${company}/search`);
        else navigate(`/business/${company}/edit/${responseAddress.id}`);
        setIsClose(false);
      } catch (error) {
        setIsClose(false);
        const splitErrors = (error as { data: { detailedMessage: string } })?.data?.detailedMessage?.split(';');
        console.log(error);
        if (splitErrors) {
          Modal.error({
            width: 550,
            title: 'Error',
            content: (
              <>
                <Row style={{ marginTop: 16 }}>
                  <Typography.Text style={{ fontSize: 16 }}>{splitErrors[0]}</Typography.Text>
                </Row>
                <Row style={{ marginTop: 16 }}>
                  <Typography.Text style={{ fontSize: 16 }}>{splitErrors[1]}</Typography.Text>
                </Row>
              </>
            )
          });
        } else {
          message.error((error as { data: { errorMessage: string } }).data.errorMessage, 5);
        }
      }
    }
  });

  /* ******************** Variables / Functions ******************** */
  const validBusinessStep = Boolean(formik.values.businessId);
  const validDeliveryAddressStep = Boolean(formik.values.addressType.length > 0 && formik.values.code && formik.values.street1 && formik.values.city && formik.values.postalCode);

  console.log(formik.values, '<<<<<<<');

  const isValidPost = validBusinessStep && validDeliveryAddressStep;

  /* ******************** Functions ******************** */

  const handleClose = (): void => {
    navigate(`/business/${selectedBusiness?.id}/search`);
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    if (close) setIsClose(true);
    await formik.submitForm();
  };

  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" disabled={isAddressCreating} onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  return (
    <FormikProvider value={formik}>
      <Spin spinning={isAddressCreating} indicator={<LoaderWithMessage loadingMessage="Creating address" />}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Row justify="space-between" style={{ marginBottom: 8 }}>
            <AntPageTitle text="Create New Address" />
            <Space>
              <Button style={{ borderRadius: 5 }} onClick={handleClose} disabled={isAddressCreating} icon={<ArrowLeftOutlined />}>
                Back to Search
              </Button>
              <Dropdown.Button
                icon={<SaveOutlined />}
                overlay={menuJSX}
                onClick={(): Promise<void> => handleSave(true)}
                loading={formik.isSubmitting}
                type="primary"
                disabled={!isValidPost || isAddressCreating}>
                Save and Exit
              </Dropdown.Button>
            </Space>
          </Row>
          <StepControls />
          <Form layout="vertical">
            <Outlet />
          </Form>
        </Space>
      </Spin>
    </FormikProvider>
  );
};
